import type { MarketplaceId } from "@nova-ai/shared"

/** Shared categorical palette for charts whose series need stable identities. */
const CHART_SERIES_COLORS = [
  "var(--color-chart-1)",
  "var(--color-chart-2)",
  "var(--color-chart-3)",
  "var(--color-chart-4)",
  "var(--color-chart-5)",
  "var(--color-chart-6)",
  "var(--color-chart-7)",
  "var(--color-chart-8)",
  "var(--color-chart-9)",
  "var(--color-chart-10)",
  "var(--color-chart-11)",
  "var(--color-chart-12)",
  "var(--color-chart-13)",
  "var(--color-chart-14)",
  "var(--color-chart-15)",
  "var(--color-chart-16)",
] as const

type SeriesColor = (typeof CHART_SERIES_COLORS)[number]
const FNV_OFFSET_BASIS = 2_166_136_261
const FNV_PRIME = 16_777_619

// One colour per marketplace, named directly rather than via an index into the
// palette. The map used to hold indices, which made "the palette must be at
// least as long as this map" a comment the compiler could not enforce: a 12th
// entry at index 11 type-checked and silently wrapped onto `carrefour-fr`'s
// colour. Keyed on the token, a new marketplace either names a real
// `SERIES_COLORS` member or fails to compile — and reusing one is at least
// visible on the line.
export const SERIES_COLOR_BY_MARKETPLACE: Readonly<Record<string, SeriesColor>> = {
  "benu-it": "var(--color-chart-14)",
  "empik-pl": "var(--color-chart-15)",
  "drmax-sk": "var(--color-chart-16)",
  "carrefour-fr": "var(--color-chart-1)",
  "cdiscount-fr": "var(--color-chart-2)",
  "decathlon-cz": "var(--color-chart-3)",
  "decathlon-de": "var(--color-chart-4)",
  "decathlon-fr": "var(--color-chart-5)",
  "decathlon-it": "var(--color-chart-6)",
  "decathlon-pl": "var(--color-chart-7)",
  "eprice-it": "var(--color-chart-12)",
  "greenweez-fr": "var(--color-chart-8)",
  "leclerc-fr": "var(--color-chart-13)",
  "shop-apotheke-de": "var(--color-chart-9)",
  "shop-apotheke-it": "var(--color-chart-10)",
  "shop-apotheke-at": "var(--color-chart-11)",
}

export const DEFAULT_MARKETPLACE_SERIES_COLOR = CHART_SERIES_COLORS[0]

export function chartSeriesColor(index: number): SeriesColor {
  return CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length] ?? CHART_SERIES_COLORS[0]
}

/** Stable categorical colour for identities whose API order can change. */
export function chartSeriesColorForKey(key: string): SeriesColor {
  let hash = FNV_OFFSET_BASIS
  for (const character of key) {
    hash ^= character.codePointAt(0) ?? 0
    hash = Math.imul(hash, FNV_PRIME)
  }
  return chartSeriesColor(hash >>> 0)
}

/**
 * A marketplace owns its chart colour. API ordering can change without
 * changing visual identity; unknown future marketplaces receive a stable
 * fallback from their position until they are added to the explicit map.
 */
export function marketplaceSeriesColor(marketplace: MarketplaceId, fallbackIndex: number): string {
  return (
    SERIES_COLOR_BY_MARKETPLACE[marketplace] ??
    chartSeriesColor(fallbackIndex) ??
    DEFAULT_MARKETPLACE_SERIES_COLOR
  )
}
